// The Explore view in a Hub address (?m=metric&d=breakdown&r=dates&from&to&c=compare&b=basis&ch=channel)
// — pure, no imports, so the API's offline gate (qa-marketing-s4) runs it.
// The address only carries the CHOICES; the API checks them against the
// catalogue (checkSpec) before anything runs. Anything malformed falls back
// to a safe default, so a pasted or old link never breaks the page.

export type Dimension = "none" | "channel" | "channel_group" | "campaign" | "lead_source" | "industry" | "month" | "week" | "rep";
export type RangePreset = "last_7" | "last_30" | "last_90" | "this_month" | "last_month" | "quarter" | "last_quarter" | "ytd" | "last_12_months" | "custom";
export type Compare = "previous" | "last_year" | "none";
export type Basis = "last_click" | "found_us";
export interface Spec {
  v: 1; metric: string; dimension: Dimension;
  range: { preset: RangePreset; from?: string; to?: string };
  compare: Compare; basis?: Basis; filters: { channel?: string };
}

export const DIMENSION_VALUES: readonly Dimension[] = ["none", "channel", "channel_group", "campaign", "lead_source", "industry", "month", "week", "rep"];
export const RANGE_VALUES: readonly RangePreset[] = ["last_7", "last_30", "last_90", "this_month", "last_month", "quarter", "last_quarter", "ytd", "last_12_months", "custom"];
export const COMPARE_VALUES: readonly Compare[] = ["previous", "last_year", "none"];
const ID = /^[a-z][a-z0-9_]{0,39}$/;
const DAY = /^(19|20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** The default first view: what every ad dollar returned, by channel, last 30 days. */
export const DEFAULT_SPEC: Spec = { v: 1, metric: "poas", dimension: "channel", range: { preset: "last_30" }, compare: "previous", basis: "last_click", filters: {} };

export function parseSpecParams(get: (k: string) => string | null): Spec {
  const m = get("m");
  if (!m || !ID.test(m)) return DEFAULT_SPEC;
  const d = get("d"); const r = get("r"); const c = get("c"); const b = get("b"); const ch = get("ch");
  const preset: RangePreset = r && (RANGE_VALUES as readonly string[]).includes(r) ? (r as RangePreset) : "last_30";
  const from = get("from"); const to = get("to");
  const range = preset === "custom" ? (from && to && DAY.test(from) && DAY.test(to) && from <= to ? { preset, from, to } : { preset: "last_30" as RangePreset }) : { preset };
  return {
    v: 1, metric: m,
    dimension: d && (DIMENSION_VALUES as readonly string[]).includes(d) ? (d as Dimension) : "none",
    range,
    compare: c && (COMPARE_VALUES as readonly string[]).includes(c) ? (c as Compare) : "previous",
    ...(b === "last_click" || b === "found_us" ? { basis: b } : {}),
    filters: ch && ID.test(ch) ? { channel: ch } : {},
  };
}

export function specQuery(s: Spec): string {
  const q = new URLSearchParams();
  q.set("m", s.metric); q.set("d", s.dimension); q.set("r", s.range.preset);
  if (s.range.preset === "custom" && s.range.from && s.range.to) { q.set("from", s.range.from); q.set("to", s.range.to); }
  q.set("c", s.compare);
  if (s.basis) q.set("b", s.basis);
  if (s.filters.channel) q.set("ch", s.filters.channel);
  return q.toString();
}
